import React, { useMemo, useState, useCallback } from 'react';
import qs, { ParsedQs } from 'qs';
import { StatusCodes } from 'http-status-codes';
import { Constrain, Clickable } from '@moda/om';
import { useLocation } from 'react-router-dom';
import { Loading } from '../../components/Loading';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import { useSkippableQuery } from '../../hooks/useSkippableQuery';
import { useUser } from '../../hooks/useUser';
import { hrefFor } from '../../routers';
import { RedirectWithStatusCode } from '../../components/RedirectWithStatusCode';
import { ResponsiveImage } from '../../components/ResponsiveImage';
import { PageMeta } from '../../components/PageMeta';
import {
  UnsubscribePageQueryNotificationPreferencesEntityFlags,
  UnsubscribePageValueFlags,
  toUpdateNotificationPreferencesMutationInput,
  toUnsubscribeNotificationPreferencesMutationInput,
  UNSUBSCRIBE_PAGE_QUERY
} from './unsubscribePage.rest';
import { UnsubscribePageSetting } from './UnsubscribePageSetting';
import { UnsubscribePageUpdateButton } from './UnsubscribePageUpdateButton';
import { UnsubscribePageUnsubscribeButton } from './UnsubscribePageUnsubscribeButton';

import './UnsubscribePage.scss';

enum Mode {
  Resting,
  Updated
}

interface WithEmailHash {
  email_hash: string;
}

type Params = WithEmailHash & ParsedQs;

export const UnsubscribePage: React.FC = () => {
  const location = useLocation();

  // If the user isn't logged in we're usually landing on this page from an unsubscribe
  // link clicked inside of an email. These contain a query param: `email_hash`, which represents
  // the relevant user's preferences.
  const { email_hash: emailHash = '' } = qs.parse(location.search.slice(1)) as Params;

  const {
    user: { isLoggedIn }
  } = useUser();

  const [mode, setMode] = useState(Mode.Resting);

  const { data, loading, error } = useSkippableQuery(UNSUBSCRIBE_PAGE_QUERY, {
    variables: {
      pathBuilder: props => {
        // The way mojo expects the `emailHash` to be serialized for logged out users
        // is a bit unconventional, so we pass in this pathBuilder variable to just encode it directly
        if (emailHash) return `${props['@rest'].path}?data[hash]=${emailHash}`;
        return props['@rest'].path;
      }
    },
    skip: !isLoggedIn && !emailHash
  });

  const [valueFlags, setFlags] = useState<UnsubscribePageValueFlags>({});

  const [entityFlags, setEntityFlags] =
    useState<UnsubscribePageQueryNotificationPreferencesEntityFlags>({});

  const handleChange = useCallback(
    (id: string, value: number) => setFlags(prevState => ({ ...prevState, [id]: value })),
    []
  );

  const handleUpdated = useCallback(() => setMode(Mode.Updated), []);
  const handleReturn = useCallback(() => setMode(Mode.Resting), []);

  const updateInput = useMemo(
    () => toUpdateNotificationPreferencesMutationInput(valueFlags, entityFlags, emailHash),
    [emailHash, entityFlags, valueFlags]
  );

  const unsubscribeInput = useMemo(
    () => toUnsubscribeNotificationPreferencesMutationInput(valueFlags, emailHash),
    [emailHash, valueFlags]
  );

  if (!isLoggedIn && !emailHash) {
    // Not logged in and no `email_hash` redirect to `/register`
    return (
      <RedirectWithStatusCode
        statusCode={StatusCodes.MOVED_TEMPORARILY}
        to={hrefFor.RegisterPage()}
      />
    );
  }

  if (error) {
    // TODO: If this errors it's likely due to a bad accessToken,
    // so possibly just redirect to /register?
    throwQueryError(error);
  }

  if (loading || !data) {
    return <Loading />;
  }

  const {
    unsubscribePage: { notificationPreferences }
  } = data;

  return (
    <>
      <PageMeta title="Unsubscribe" />
      <div className="UnsubscribePage">
        <Constrain className="UnsubscribePage__content">
          <div className="UnsubscribePage__pane UnsubscribePage__pane--banner">
            <ResponsiveImage
              alt="Unsubscribe"
              src="/dist/public/unsubscribe_banner.jpg"
              aspectWidth={1084}
              aspectHeight={1200}
              maxWidth={800}
              maxHeight={800}
              srcMode="static"
            />
          </div>

          <div className="UnsubscribePage__pane">
            <h1 className="UnsubscribePage__title">
              {mode === Mode.Updated
                ? 'Your Subscriptions Have Been Updated'
                : 'Manage My Subscriptions'}
            </h1>

            {mode !== Mode.Updated && (
              <div className="UnsubscribePage__options">
                {notificationPreferences.map(setting => (
                  <UnsubscribePageSetting
                    key={setting.id}
                    setting={setting}
                    onChange={handleChange}
                    onChangeEntitiesToRemove={setEntityFlags}
                  />
                ))}
              </div>
            )}

            {mode === Mode.Updated && (
              <div className="UnsubscribePage__success">
                <p>
                  All changes to email preferences can take up to 5-7 business days to take effect.
                </p>
                <p>
                  To make other changes,{' '}
                  <Clickable className="UnsubscribePage__return" onClick={handleReturn}>
                    return to your e-mail preferences.
                  </Clickable>
                </p>
              </div>
            )}

            {mode !== Mode.Updated && (
              <div className="UnsubscribePage__actions">
                <UnsubscribePageUnsubscribeButton
                  input={unsubscribeInput}
                  onUpdated={handleUpdated}
                />

                <UnsubscribePageUpdateButton input={updateInput} onUpdated={handleUpdated} />
              </div>
            )}
          </div>
        </Constrain>
      </div>
    </>
  );
};
